import { getCanonicalReadingText } from '../../components/IELTS/utils/readingAnnotations';

export const categoryLabels = {
  'mcq-single': 'Multiple Choice: Single Answer', 'mcq-multiple': 'Multiple Choice: Multiple Answers',
  truefalse: 'True / False / Not Given', yesno: 'Yes / No / Not Given', matching: 'Matching',
  'matching-headings': 'Matching Headings', 'matching-information': 'Matching Information',
  'matching-features': 'Matching Features', 'matching-sentence-endings': 'Matching Sentence Endings',
  completion: 'Completion', 'sentence-completion': 'Sentence Completion', 'summary-completion': 'Summary Completion',
  'note-completion': 'Note Completion', 'table-completion': 'Table Completion', 'form-completion': 'Form Completion',
  'flow-chart-completion': 'Flow Chart Completion', 'diagram-labeling': 'Diagram Labeling',
  'map-labeling': 'Map Labeling', 'plan-labeling': 'Plan Labeling', 'visual-labeling': 'Visual Labeling',
  'short-answer': 'Short Answer', 'bar-chart': 'Bar Chart', 'line-graph': 'Line Graph', 'pie-chart': 'Pie Chart',
  table: 'Table', map: 'Map', process: 'Process', 'mixed-charts': 'Mixed Charts', letter: 'Letter',
  opinion: 'Opinion Essay', discussion: 'Discussion Essay', 'advantages-disadvantages': 'Advantages and Disadvantages',
  'problem-solution': 'Problem and Solution', 'two-part-question': 'Two-Part Question',
  'positive-negative-development': 'Positive or Negative Development',
  'part-1': 'Part 1', 'part-2': 'Part 2', 'part-3': 'Part 3',
};

// Results can be reopened from their immutable review snapshot even when the
// live catalogue exercise has since changed or become unavailable.
export function miniReviewDetail(attempt) {
  if (!['completed', 'failed', 'evaluation_failed'].includes(attempt.status)) return null;
  const review = attempt.result?.review || attempt.review;
  if (!review?.exercise) return null;
  if (Array.isArray(review.sections)) return review;
  if (!review.part) return null;
  return { ...review, sections: [{ ...review.part, questions: review.questions || [] }] };
}

export function miniSections(detail) {
  const module = detail.exercise.module;
  return detail.sections.map(section => {
    const questionsHtml = (section.questions || []).map(question => question.question_html || '').join('\n');
    const objective = module === 'Reading' || module === 'Listening';
    let passage = section.passage_html || section.passage_text || '';
    let highlights = section.answer_highlights || section.answerHighlights || [];
    if (typeof highlights === 'string') {
      try { highlights = JSON.parse(highlights); } catch { highlights = []; }
    }
    highlights = Array.isArray(highlights) ? highlights.map(item => ({ ...item })) : [];
    const numbers = detail.exercise.question_numbers;
    // Shared passages can contain heading targets from other question groups.
    if (module === 'Reading' && Array.isArray(numbers) && numbers.length) {
      const scope = new Set(numbers.map(String));
      const doc = new DOMParser().parseFromString(passage, 'text/html');
      // Keep offset-only highlights aligned through every text deletion, even
      // when identical answer words occur more than once in the passage.
      const removeRange = range => {
        const prefix = doc.createRange();
        prefix.selectNodeContents(doc.body);
        prefix.setEnd(range.startContainer, range.startOffset);
        const start = getCanonicalReadingText(prefix.cloneContents()).length;
        const length = getCanonicalReadingText(range.cloneContents()).length;
        highlights.forEach(item => {
          for (const key of ['start', 'end']) {
            const offset = Number(item[key]);
            item[key] = offset - Math.min(Math.max(offset - start, 0), length);
          }
        });
        range.deleteContents();
      };
      doc.querySelectorAll('.drag-drop-field').forEach(field => {
        const number = field.getAttribute('data-question-number') || field.id.match(/^drag-(\d{1,2})(?:-|$)/)?.[1];
        if (number && !scope.has(number)) {
          const range = doc.createRange();
          range.selectNode(field);
          removeRange(range);
        }
      });
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      const texts = [];
      while (walker.nextNode()) texts.push(walker.currentNode);
      texts.forEach(node => {
        const paragraph = node.parentElement?.closest('p');
        const markerOnly = paragraph?.textContent.match(/^\s*(?:\d+\s*)?\[Drop here for Q\s*(\d+)\]\s*$/i);
        if (!doc.body.contains(node)) return;
        if (markerOnly && !scope.has(markerOnly[1])) {
          const range = doc.createRange();
          range.selectNodeContents(paragraph);
          removeRange(range);
        } else {
          const markers = [...node.nodeValue.matchAll(/\[Drop here for Q\s*(\d+)\]/gi)].reverse();
          markers.forEach(match => {
            if (scope.has(match[1])) return;
            const range = doc.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            removeRange(range);
          });
        }
      });
      passage = doc.body.innerHTML;
    }
    return {
      ...section,
      ...(module === 'Reading' ? { passage_html: passage, miniQuestionNumbers: numbers, answer_highlights: highlights } : {}),
      // Full-test passage_text may contain every group in a part. The selected
      // database rows contain the complete group and must be the only inputs.
      passage_text: objective && questionsHtml.trim() ? questionsHtml
        : section.passage_text || section.passage_html || questionsHtml,
      part_name: section.part_name || `${module === 'Writing' ? 'Task' : 'Part'} ${detail.partIndex + 1}`,
    };
  });
}
